package components.project.configuration.powerusage

import components.project.configuration.additional.einspeiseArtForm
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.components.Einspeiseart
import it.neckar.lizergy.model.configuration.energy.power.PowerUsageScenario
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.energy.AmountOfEnergy
import it.neckar.open.formatting.formatAsPercentage
import it.neckar.open.unit.other.pct
import it.neckar.open.unit.other.pct100
import it.neckar.open.unit.si.kWh
import it.neckar.open.unit.si.km
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import plannerI18nConfiguration
import react.*
import react.dom.*

/**
 *
 */
fun RBuilder.powerUsageForm(
  quoteConfiguration: QuoteConfiguration?,
  yearlyPowerConsumptionState: StateInstance<@kWh Int>,
  waermepumpeConsumptionState: StateInstance<@kWh Int>,
  electricCarUsageState: StateInstance<@km Int>,
  einspeiseartToSave: StateInstance<Einspeiseart>,
  manualPowerConsumptionState: StateInstance<@pct100 Int?>,
  editableStatus: EditableStatus,
): Unit = child(PowerUsageForm) {
  attrs {
    this.quoteConfiguration = quoteConfiguration
    this.yearlyPowerConsumptionState = yearlyPowerConsumptionState
    this.waermepumpeConsumptionState = waermepumpeConsumptionState
    this.electricCarUsageState = electricCarUsageState
    this.einspeiseartToSave = einspeiseartToSave
    this.manualPowerConsumptionState = manualPowerConsumptionState
    this.editableStatus = editableStatus
  }
}

val PowerUsageForm: FC<PowerUsageFormProps> = fc("PowerUsageForm") { props ->
  val quoteConfiguration = props::quoteConfiguration.safeGet()

  val yearlyPowerConsumptionState = props::yearlyPowerConsumptionState.getNotNull()
  val waermepumpeConsumptionState = props::waermepumpeConsumptionState.getNotNull()
  val electricCarUsageState = props::electricCarUsageState.getNotNull()
  val einspeiseartToSave = props::einspeiseartToSave.getNotNull()
  val manualPowerConsumptionState = props::manualPowerConsumptionState.getNotNull()

  val editableStatus = props::editableStatus.safeGet()

  val houseEnergyConsumption = AmountOfEnergy(yearlyPowerConsumptionState.value + waermepumpeConsumptionState.value)
  val carEnergyConsumption = (PowerUsageScenario.electricCarConsumptionPerKilometer * electricCarUsageState.value)
  val totalEnergyConsumption = houseEnergyConsumption + carEnergyConsumption


  // Calculate the power consumption provided by PV
  val calculatedPowerConsumptionProvidedByPvPercentage: @pct Double? = useMemo(quoteConfiguration, yearlyPowerConsumptionState.value, waermepumpeConsumptionState.value, electricCarUsageState.value) {
    quoteConfiguration?.copy(
      powerUsageScenario = quoteConfiguration.powerUsageScenario.copy(
        yearlyPowerConsumption = AmountOfEnergy.kWh(yearlyPowerConsumptionState.value.toDouble()),
        waermepumpeConsumption = AmountOfEnergy.kWh(waermepumpeConsumptionState.value.toDouble()),
        electricCarUsage = electricCarUsageState.value.toDouble(),
      ),
      manualPowerConsumptionDistribution = null,
    )
  }?.powerConsumptionDistribution?.selfProducedPercentage


  div("my-3") {
    einspeiseArtForm(einspeiseartToSave, editableStatus)
  }

  if (einspeiseartToSave.value == Einspeiseart.Ueberschuss) {
    div("form-floating mt-3") {
      div("row gx-0") {
        div("col-lg-4") {
          div("input-group") {
            floatingIntInputField(
              valueAndSetter = yearlyPowerConsumptionState,
              fieldName = "yearlyPowerConsumption",
              title = "Jahresverbrauch",
              numberConstraint = ZeroOrPositive,
              editableStatus = editableStatus,
            ) {
              attrs {
                mergedRight()
              }
            }
            span("input-group-text") {
              +"kWh"
            }
          }
        }

        div("col-lg-4") {
          div("input-group") {
            floatingIntInputField(
              valueAndSetter = waermepumpeConsumptionState,
              fieldName = "waermepumpeConsumption",
              title = "Verbrauch Wärmepumpe",
              numberConstraint = ZeroOrPositive,
              editableStatus = editableStatus,
            ) {
              attrs {
                mergedLeft()
                mergedRight()
              }
            }
            span("input-group-text") {
              +"kWh"
            }
          }
        }

        div("col-lg-4") {
          div("input-group") {
            floatingIntInputField(
              valueAndSetter = electricCarUsageState,
              fieldName = "electricCarUsage",
              title = "E-Auto Nutzung",
              numberConstraint = ZeroOrPositive,
              editableStatus = editableStatus,
            ) {
              attrs {
                mergedLeft()
              }
            }
            span("input-group-text") {
              +"km"
            }
          }
        }
      }
    }

    p("form-text") {
      +"Gesamt: ${totalEnergyConsumption.formatKiloWattHour(plannerI18nConfiguration)} (Jahresverbrauch & Wärmepumpe: ${houseEnergyConsumption.formatKiloWattHour(plannerI18nConfiguration)} + E-Auto: ${carEnergyConsumption.formatKiloWattHour(plannerI18nConfiguration)})"
    }

    div("form-floating my-3") {
      nullableFloatingIntInputField(
        valueAndSetter = manualPowerConsumptionState,
        fieldName = "yearlyPowerConsumption",
        title = "${calculatedPowerConsumptionProvidedByPvPercentage?.formatAsPercentage(0) ?: "-"} Autarkiegrad",
        numberConstraint = ZeroOrPositive,
        editableStatus = editableStatus,
      )
    }
  }

}

external interface PowerUsageFormProps : Props {
  var quoteConfiguration: QuoteConfiguration?
  var yearlyPowerConsumptionState: StateInstance<@kWh Int>
  var waermepumpeConsumptionState: StateInstance<@kWh Int>
  var electricCarUsageState: StateInstance<@km Int>
  var einspeiseartToSave: StateInstance<Einspeiseart>
  var manualPowerConsumptionState: StateInstance<@pct100 Int?>
  var editableStatus: EditableStatus
}
